import { useEffect, useRef, useState } from "react";
import { AD_BUTTONS, ALTS_MAX, CARDS_MAX, CARDS_MIN, type AdCard, type AdFormat } from "../../../shared/goals/ready";
import { Icon } from "../../components/ui/Icon";
import { ApiError } from "../../lib/api";
import { readyApi, type ReadyAd } from "../../lib/readyApi";
import { ProductMark, type ProductRef } from "../goal/FlowHead";
import { AdPlayer } from "./AdPlayer";
import { PhoneQr } from "./PhoneQr";

// Why: a card is drawn again after every save; the preview it showed (feed or Stories and Reels) stays.
const shown = new Map<string, "feed" | "stories">();
import { ScriptLines, scriptLines } from "./ScriptLines";
import { ScriptReader } from "./ScriptReader";
import { canRecordHere } from "./useRecorder";

/**
 * One ad on the Ads step, as people will see it, by its format (notes/AUDIENCE.md 5.10, prototype v29): one
 * video or photo with other words Meta can try and a Stories and Reels version, photos people swipe with a
 * headline and line per card, or a published post with its likes and comments.
 */

export const FORMATS: [AdFormat, string][] = [
  ["camera", "You on camera"],
  ["screen", "A screen recording"],
  ["clip", "A clip from footage you have"],
  ["image", "One photo"],
  ["carousel", "Photos people swipe"],
  ["post", "Your published post"],
];
/** The formats an idea of the owner's can take; a published post is picked from the Page instead. */
export const IDEA_FORMATS = FORMATS.filter(([k]) => k !== "post");
const ORIGIN: Record<string, [string, string]> = {
  video: ["ot-video", "Your video"],
  post: ["ot-video", "Your post"],
  owner: ["ot-idea", "Your idea"],
  plan: ["ot-plan", "Idea from the plan"],
  agent: ["ot-plan", "Idea from the plan"],
};
const MEDIA_TYPES = "video/mp4,video/quicktime,image/jpeg,image/png,image/webp";
const PHOTO_TYPES = "image/jpeg,image/png,image/webp";

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const newCardId = () => `c${Math.random().toString(36).slice(2, 10)}`;
export const postDay = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

export function AdItem({
  goalId,
  ad,
  product,
  opens,
  opensPage,
  stoppedWhy,
  onChanged,
  onRemoved,
  onDraftAgain,
  onPickPost,
  say,
  recordTall,
  onRecordOpened,
}: {
  goalId: string;
  ad: ReadyAd;
  product: ProductRef | null;
  opens: string;
  /** Where the ads open in full, for the Opens field: the page, not only the site. */
  opensPage: string;
  /** Why the agent's writing run stopped, when it did: a card still waiting for its words says so. */
  stoppedWhy: string | null;
  /** Read the ads again; an action holds the card until the new revision is in. */
  onChanged: () => Promise<void>;
  onRemoved: (adId: string) => void;
  onDraftAgain: () => void;
  onPickPost: (ad: ReadyAd) => void;
  say: (m: string, ok?: boolean) => void;
  /** Opened from the code a laptop showed: record the Stories and Reels version here. */
  recordTall?: boolean;
  onRecordOpened?: () => void;
}) {
  const w = ad.words;
  const format = w.format ?? "camera";
  const [form, setForm] = useState({ primary: w.primary ?? "", headline: w.headline ?? "", says: w.says ?? "" });
  const [cards, setCards] = useState<AdCard[]>(w.cards ?? []);
  const [alts, setAlts] = useState(w.alts ?? []);
  const [ai, setAi] = useState(!!w.ai);
  // Why: Read it lets the take's shape choose; Record (feed) or Record it (Stories and Reels) choose the version.
  const [reading, setReading] = useState<{ lines: string[]; to: "main" | "tall" | "auto"; camera: boolean } | null>(null);
  const [mainWide, setMainWide] = useState<boolean | null>(null);
  const [phoneCode, setPhoneCode] = useState(false);
  const [view, setViewState] = useState(() => shown.get(ad.id) ?? "feed");
  const setView = (v: "feed" | "stories") => {
    shown.set(ad.id, v);
    setViewState(v);
  };
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const tallFile = useRef<HTMLInputElement>(null);
  const cardFile = useRef<HTMLInputElement>(null);
  const cardFor = useRef<string | null>(null);
  useEffect(() => setForm({ primary: w.primary ?? "", headline: w.headline ?? "", says: w.says ?? "" }), [w.primary, w.headline, w.says]);
  useEffect(() => setCards(w.cards ?? []), [w.cards]);
  useEffect(() => setAlts(w.alts ?? []), [w.alts]);
  useEffect(() => setAi(!!w.ai), [w.ai]);
  const wantsAlts = w.wants_alts ?? 0;
  // Why: a card waiting only for other words it asked for already has its own words, and shows them.
  const writing = ad.status === "drafting" && !(w.primary && wantsAlts > 0);
  const single = format !== "carousel" && format !== "post";
  const camera = format === "camera";
  const image = (ad.asset?.contentType ?? "").startsWith("image/") || format === "image";
  const cta = w.cta ?? "SIGN_UP";
  const [cls, origin] =
    ad.origin === "video" && format === "carousel" ? ["ot-video", "Your photos"] : ad.origin === "video" && image ? ["ot-video", "Your photo"] : (ORIGIN[ad.origin] ?? ORIGIN.owner);

  const act = async (f: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await f();
      await onChanged();
    } catch (e) {
      say(message(e), false);
      await onChanged();
    } finally {
      setBusy(false);
    }
  };
  const save = (words: Record<string, unknown>) => void act(() => readyApi.editAd(goalId, ad.id, ad.rev, words as never));
  const blur = (field: "primary" | "headline" | "says") => {
    const v = form[field].trim();
    if (v === (w[field] ?? "")) return;
    if (!v && field !== "says") return setForm((f) => ({ ...f, [field]: w[field] ?? "" }));
    save({ [field]: v || null });
  };
  const saveCards = (next: AdCard[]) => {
    if (JSON.stringify(next) !== JSON.stringify(w.cards ?? [])) save({ cards: next });
  };
  const saveAlts = (next: typeof alts) => {
    if (next.some((a) => !a.primary.trim() || !a.headline.trim())) return setAlts(w.alts ?? []);
    if (JSON.stringify(next) !== JSON.stringify(w.alts ?? [])) save({ alts: next.length ? next : null });
  };
  const upload = (f: File, slot: string) => void act(() => readyApi.upload(goalId, ad.id, ad.rev, f, slot));
  // Why: Stories and Reels want an upright take, which a phone records; a laptop shows a code that opens this ad there.
  const recordUpright = () => {
    if (matchMedia("(pointer: coarse)").matches) setReading({ lines: scriptLines(w.says ?? ""), to: "tall", camera: true });
    else setPhoneCode((on) => !on);
  };
  const recordFeed = () => setReading({ lines: scriptLines(w.says ?? ""), to: "main", camera: true });
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when the card first shows, from the code's address.
  useEffect(() => {
    if (!recordTall || format !== "camera" || !ad.asset) return;
    setView("stories");
    setReading({ lines: scriptLines(w.says ?? ""), to: "tall", camera: true });
    onRecordOpened?.();
  }, []);

  const preview = () => {
    if (format === "carousel")
      return (
        <div className="car" aria-label="The cards, swiped sideways">
          {cards.map((c, i) => (
            <div className="car-c" key={c.id}>
              <div className={`car-m${ad.files.cards[c.id] ? " has" : ""}`}>
                {ad.files.cards[c.id] ? <img src={readyApi.videoUrl(goalId, ad.id, ad.rev, `card:${c.id}`)} alt="" /> : <span className="vh">Card {i + 1}</span>}
              </div>
              <div className="car-f">
                <div>
                  <b>{c.headline || "..."}</b>
                  <span>{c.description}</span>
                </div>
                <span className="cta">{AD_BUTTONS[cta]}</span>
              </div>
            </div>
          ))}
        </div>
      );
    if (format === "post")
      return (
        <div className="post-m has">
          {w.post?.picture ? <img src={w.post.picture} alt="" referrerPolicy="no-referrer" /> : <span className="vl">{w.post?.kind}</span>}
        </div>
      );
    return (
      <div className={`post-m${ad.asset ? " has" : ""}${ad.asset && !image ? " vid" : ""}`}>
        {ad.asset ? (
          image ? (
            <img src={readyApi.videoUrl(goalId, ad.id, ad.rev)} alt="" />
          ) : (
            <AdPlayer src={readyApi.videoUrl(goalId, ad.id, ad.rev)} label="The ad's video" onShape={setMainWide} />
          )
        ) : (
          <span className="vh">{image ? "Your photo shows here" : "Your video shows here"}</span>
        )}
      </div>
    );
  };

  return (
    <article className="adc" id={`ad-${ad.id}`}>
      <div className="adc-h">
        <div>
          <b>{w.name ?? w.idea ?? "Your ad"}</b>
          <div className="adc-tags">
            <span className={`ot ${cls}`}>{origin}</span>
            <span className="m">{FORMATS.find(([k]) => k === format)?.[1]}</span>
          </div>
        </div>
        <button className="x" type="button" aria-label="Remove this ad" disabled={busy} onClick={() => void act(() => readyApi.removeIdea(goalId, ad.id)).then(() => onRemoved(ad.id))}>
          <Icon name="x" />
        </button>
      </div>
      <div className="adc-b">
        <div className="pv">
        {single && (
          <div className="pv-tabs" role="group" aria-label="Where it shows">
            <button type="button" aria-pressed={view === "feed"} onClick={() => setView("feed")}>
              Feed
            </button>
            <button type="button" aria-pressed={view === "stories"} onClick={() => setView("stories")}>
              Stories and Reels
            </button>
          </div>
        )}
        {single && view === "stories" ? (
          <div className="story" aria-label="Preview: Stories and Reels">
            <div className="story-m">
              {!ad.asset ? (
                <span className="vh">{image ? "Your upright photo shows here" : "Your upright video shows here"}</span>
              ) : image ? (
                <img src={readyApi.videoUrl(goalId, ad.id, ad.rev, ad.files.tall ? "tall" : "main")} alt="" />
              ) : (
                <AdPlayer
                  src={readyApi.videoUrl(goalId, ad.id, ad.rev, ad.files.tall ? "tall" : "main")}
                  label={ad.files.tall ? "The Stories and Reels version" : "The ad's video, as Stories and Reels show it"}
                  fill
                />
              )}
            </div>
            <div className="story-h">
              {product && <ProductMark product={product} />}
              <b>{product?.name ?? ""}</b>
              <span>Sponsored</span>
            </div>
            <span className="story-cta">{AD_BUTTONS[cta]}</span>
          </div>
        ) : (
        <div className="post" aria-label="Preview: what people see">
          <div className="post-h">
            {product && <ProductMark product={product} />}
            <div>
              <b>{product?.name ?? ""}</b>
              <span>Sponsored</span>
            </div>
          </div>
          {writing && stoppedWhy !== null ? (
            <p className="post-t drafting">{`The agent stopped before writing these words. ${stoppedWhy}`.trim()}</p>
          ) : writing ? (
            <p className="post-t drafting">
              <span className="spin" />
              Writing the words
            </p>
          ) : (
            <p className="post-t">{format === "post" ? w.post?.text : form.primary}</p>
          )}
          {preview()}
          {format !== "carousel" && (
            <div className="post-f">
              <div>
                <span className="u">{opens}</span>
                <b>{writing ? "..." : form.headline}</b>
              </div>
              <span className="cta">{AD_BUTTONS[cta]}</span>
            </div>
          )}
          {format === "post" && w.post && (
            <div className="post-s">
              {w.post.reactions} reactions · {w.post.comments} comments
            </div>
          )}
        </div>
        )}
        {single && (
          <div className="pv-acts">
            <input
              ref={file}
              type="file"
              hidden
              accept={MEDIA_TYPES}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) upload(f, "main");
              }}
            />
            <input
              ref={tallFile}
              type="file"
              hidden
              accept={image ? PHOTO_TYPES : "video/mp4,video/quicktime"}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) upload(f, "tall");
              }}
            />
            {view === "feed" ? (
              ad.asset ? (
                <>
                  <span className="vok">
                    <Icon name="check" />
                    {ad.origin === "video" ? (image ? "Your photo" : "Your video") : "Added"}
                  </span>
                  {camera && (
                    <button className="btn sm ghost" type="button" disabled={busy} onClick={recordFeed}>
                      <Icon name="film" />
                      Record again
                    </button>
                  )}
                  <button className="btn sm ghost" type="button" disabled={busy} onClick={() => file.current?.click()}>
                    Replace
                  </button>
                </>
              ) : (
                <>
                  {camera && (
                    <button className="btn sm" type="button" disabled={busy} onClick={recordFeed}>
                      <Icon name="film" />
                      Record
                    </button>
                  )}
                  <button className={camera ? "btn sm ghost" : "btn sm"} type="button" disabled={busy} onClick={() => file.current?.click()}>
                    <Icon name="upload" />
                    {image ? "Add the photo" : "Add the video"}
                  </button>
                </>
              )
            ) : !ad.asset ? (
              <span className="hint">Add the feed version first.</span>
            ) : ad.files.tall ? (
              <>
                <span className="vok">
                  <Icon name="check" />
                  Added
                </span>
                {camera && (
                  <button className="btn sm ghost" type="button" disabled={busy} aria-expanded={phoneCode} onClick={recordUpright}>
                    <Icon name="phone" />
                    Record again
                  </button>
                )}
                <button className="btn sm ghost" type="button" disabled={busy} onClick={() => tallFile.current?.click()}>
                  Replace
                </button>
                <button className="btn sm ghost" type="button" disabled={busy} onClick={() => void act(() => readyApi.removeTall(goalId, ad.id, ad.rev))}>
                  Remove
                </button>
              </>
            ) : (
              <>
                {camera && (
                  <button className="btn sm" type="button" disabled={busy} aria-expanded={phoneCode} onClick={recordUpright}>
                    <Icon name="phone" />
                    Record it
                  </button>
                )}
                <button className={camera ? "btn sm ghost" : "btn sm"} type="button" disabled={busy} onClick={() => tallFile.current?.click()}>
                  <Icon name="upload" />
                  {image ? "Add the upright photo" : "Add the upright version"}
                </button>
              </>
            )}
          </div>
        )}
        {single && view === "stories" && phoneCode && <PhoneQr url={`${location.origin}/goals/${goalId}/ads?record=${ad.id}`} />}
        {single && ad.asset && view === "stories" && !ad.files.tall && <p className="pv-note">Without an upright version (9:16), Meta shows the wide one here with space above and below.</p>}
        </div>
        <div className="adc-side">
          {format === "carousel" && (
            <div className="af">
              <span className="afl">
                The cards <span className="optl">2 to 10</span>
              </span>
              <input
                ref={cardFile}
                type="file"
                hidden
                accept={PHOTO_TYPES}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f && cardFor.current) upload(f, `card:${cardFor.current}`);
                }}
              />
              <div className="cards">
                {cards.map((c, i) => (
                  <div className="cardr" key={c.id}>
                    <button
                      className={`cph${ad.files.cards[c.id] ? " has" : ""}`}
                      type="button"
                      disabled={busy}
                      aria-label={`${ad.files.cards[c.id] ? "Replace" : "Add"} the photo for card ${i + 1}`}
                      onClick={() => {
                        cardFor.current = c.id;
                        cardFile.current?.click();
                      }}
                    >
                      {ad.files.cards[c.id] ? <img src={readyApi.videoUrl(goalId, ad.id, ad.rev, `card:${c.id}`)} alt="" /> : <Icon name="upload" />}
                    </button>
                    <div className="cwords">
                      <input
                        value={c.headline}
                        placeholder="Headline"
                        aria-label={`Card ${i + 1} headline`}
                        maxLength={60}
                        disabled={writing}
                        onChange={(e) => setCards(cards.map((x) => (x.id === c.id ? { ...x, headline: e.target.value } : x)))}
                        onBlur={() => saveCards(cards)}
                      />
                      <input
                        value={c.description}
                        placeholder="One line under it"
                        aria-label={`Card ${i + 1} line`}
                        maxLength={80}
                        disabled={writing}
                        onChange={(e) => setCards(cards.map((x) => (x.id === c.id ? { ...x, description: e.target.value } : x)))}
                        onBlur={() => saveCards(cards)}
                      />
                    </div>
                    {cards.length > CARDS_MIN ? (
                      <button className="fx" type="button" disabled={busy} aria-label={`Remove card ${i + 1}`} onClick={() => saveCards(cards.filter((x) => x.id !== c.id))}>
                        <Icon name="x" />
                      </button>
                    ) : (
                      <span />
                    )}
                  </div>
                ))}
              </div>
              {cards.length < CARDS_MAX && (
                <button className="add" type="button" disabled={busy} onClick={() => saveCards([...cards, { id: newCardId(), headline: "", description: "" }])}>
                  <Icon name="plus" />
                  Add a card
                </button>
              )}
              <span className="hint">Square photos, 1080 by 1080.</span>
            </div>
          )}
          {format === "post" && w.post && (
            <div className="af">
              <span className="afl">The post</span>
              <div className="pst">
                <span className="pst-k">{w.post.kind}</span>
                <div>
                  <b>Published {postDay(w.post.at)}</b>
                  <p>{w.post.text}</p>
                </div>
                <button className="btn sm ghost" type="button" disabled={busy} onClick={() => onPickPost(ad)}>
                  Change
                </button>
              </div>
              <span className="hint">Its words, likes and comments stay as they are on Facebook.</span>
            </div>
          )}
          {!writing && format !== "post" && (
            <>
              <label className="af">
                <span className="afl">Ad text</span>
                <textarea rows={3} value={form.primary} onChange={(e) => setForm((f) => ({ ...f, primary: e.target.value }))} onBlur={() => blur("primary")} />
              </label>
              {format !== "carousel" && (
                <label className="af">
                  <span className="afl">Headline</span>
                  <input value={form.headline} onChange={(e) => setForm((f) => ({ ...f, headline: e.target.value }))} onBlur={() => blur("headline")} />
                </label>
              )}
            </>
          )}
          {!writing && (
            <div className="af2">
              <label className="af">
                <span className="afl">Button</span>
                <select value={cta} onChange={(e) => save({ cta: e.target.value })}>
                  {Object.entries(AD_BUTTONS).map(([k, l]) => (
                    <option key={k} value={k}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <div className="af">
                <span className="afl">Opens</span>
                <span className="afv">
                  <span title={opensPage}>{opensPage || "Not set yet"}</span>
                  <button className="btn ghost sm" type="button" onClick={() => document.getElementById("y-dest")?.scrollIntoView({ behavior: "smooth", block: "center" })}>
                    Change
                  </button>
                </span>
              </div>
            </div>
          )}
          {!writing && single && (
            <div className="more">
              {(alts.length > 0 || wantsAlts > 0) && (
                <div className="af">
                  <span className="afl">
                    Other words Meta can try <span className="optl">up to {ALTS_MAX}</span>
                  </span>
                  {alts.map((x, i) => (
                    <div className="alt" key={i}>
                      <div className="cwords">
                        <textarea
                          rows={2}
                          value={x.primary}
                          aria-label={`Other ad text ${i + 1}`}
                          onChange={(e) => setAlts(alts.map((y, k) => (k === i ? { ...y, primary: e.target.value } : y)))}
                          onBlur={() => saveAlts(alts)}
                        />
                        <input
                          value={x.headline}
                          aria-label={`Other headline ${i + 1}`}
                          maxLength={60}
                          onChange={(e) => setAlts(alts.map((y, k) => (k === i ? { ...y, headline: e.target.value } : y)))}
                          onBlur={() => saveAlts(alts)}
                        />
                      </div>
                      <button className="fx" type="button" disabled={busy || wantsAlts > 0} aria-label="Remove these words" onClick={() => saveAlts(alts.filter((_, k) => k !== i))}>
                        <Icon name="x" />
                      </button>
                    </div>
                  ))}
                  {Array.from({ length: wantsAlts }, (_, i) => (
                    <p className="drafting" key={`w${i}`}>
                      {stoppedWhy !== null ? (
                        `The agent stopped before writing these words. ${stoppedWhy}`.trim()
                      ) : (
                        <>
                          <span className="spin" />
                          Writing it
                        </>
                      )}
                    </p>
                  ))}
                  {wantsAlts > 0 && stoppedWhy !== null && (
                    <button className="btn sm" type="button" onClick={onDraftAgain} style={{ alignSelf: "flex-start" }}>
                      Write the words again
                    </button>
                  )}
                </div>
              )}
              <div className="chips">
                {alts.length + wantsAlts < ALTS_MAX && (
                  <button className="add" type="button" disabled={busy || wantsAlts > 0} onClick={() => void act(() => readyApi.askAlts(goalId, ad.id))}>
                    <Icon name="plus" />
                    Other words Meta can try
                  </button>
                )}
              </div>
            </div>
          )}
          {writing && stoppedWhy !== null && (
            <button className="btn sm" type="button" onClick={onDraftAgain} style={{ alignSelf: "flex-start" }}>
              Write the words again
            </button>
          )}
          {w.why && (ad.origin === "plan" || ad.origin === "agent") && (
            <p className="why">
              <b>Why this idea:</b> {w.why}
            </p>
          )}
          {format === "camera" &&
            !writing &&
            (w.says !== undefined ? (
              <ScriptLines says={w.says} busy={busy} onSave={(says) => save({ says: says || null })} onRead={(lines) => setReading({ lines, to: "auto", camera: false })} />
            ) : (
              <div className="scr none">
                <span className="lbl">
                  A script to read <span className="optl">optional</span>
                </span>
                {ad.touched ? (
                  <textarea rows={3} value={form.says} onChange={(e) => setForm((f) => ({ ...f, says: e.target.value }))} onBlur={() => blur("says")} />
                ) : (
                  <button className="btn sm" type="button" disabled={busy} onClick={() => void act(() => readyApi.askScript(goalId, ad.id))}>
                    <Icon name="spark" />
                    Draft a script from my idea
                  </button>
                )}
              </div>
            ))}
          {!writing && format !== "post" && (
            <label className="aiq">
              <input
                type="checkbox"
                checked={ai}
                disabled={busy}
                onChange={(e) => {
                  setAi(e.target.checked);
                  save({ ai: e.target.checked ? true : null });
                }}
              />
              <span>Made or edited with AI</span>
            </label>
          )}
          {reading && (
            <ScriptReader
              lines={reading.lines}
              canRecord={canRecordHere()}
              startCamera={reading.camera}
              // Why: from Read it, an upright take goes to Stories and Reels when the ad's video is already wide; asked for there, only an upright one does.
              slotFor={(wide) => (reading.to === "main" ? "main" : reading.to === "tall" ? (wide ? null : "tall") : !wide && mainWide ? "tall" : "main")}
              onUse={async (take, slot) => {
                await readyApi.upload(goalId, ad.id, ad.rev, take, slot);
                if (slot === "tall") shown.set(ad.id, "stories");
                await onChanged();
              }}
              onClose={() => setReading(null)}
            />
          )}
        </div>
      </div>
    </article>
  );
}
